/**
 * Offline utilities for client-side use
 * This file contains functions for registering the service worker and detecting offline status
 * Note: This file should only be imported in client-side code (e.g., useEffect)
 */

// Register the service worker
export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then(registration => {
          console.log('Service Worker registered with scope:', registration.scope);
          
          // Check if there's a waiting service worker
          if (registration.waiting) {
            // New content is available, but the user needs to refresh
            notifyUserOfUpdate(registration);
          }
          
          // Handle updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New content is available, but the user needs to refresh
                  notifyUserOfUpdate(registration);
                }
              });
            }
          });
        })
        .catch(error => {
          console.error('Service Worker registration failed:', error);
        });
    });
    
    // Handle service worker updates
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // The service worker has taken control, reload for fresh content
      window.location.reload();
    });
  }
}

// Notify the user that an update is available
function notifyUserOfUpdate(registration: ServiceWorkerRegistration) {
  // Create a custom event that the app can listen for
  const updateEvent = new CustomEvent('serviceWorkerUpdateAvailable', {
    detail: registration
  });
  window.dispatchEvent(updateEvent);
}

// Check if the user is online
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine;
}

// Add event listeners for online/offline events
export function setupOfflineListeners(
  onOffline: () => void,
  onOnline: () => void
): () => void {
  const handleOffline = () => {
    onOffline();
  };
  
  const handleOnline = () => {
    onOnline();
  };
  
  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);
  
  // Return a cleanup function
  return () => {
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
  };
}

// Create a component to show when a new version is available
export function createUpdateNotification(registration: ServiceWorkerRegistration) {
  const updateServiceWorker = () => {
    if (registration.waiting) {
      // Send a message to the waiting service worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  };
  
  return {
    message: 'A new version is available!',
    action: updateServiceWorker
  };
}

// Save data for offline use
export function saveForOffline(key: string, data: any): void {
  try {
    localStorage.setItem(`offline_${key}`, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save data for offline use:', error);
  }
}

// Get data saved for offline use
export function getOfflineData(key: string): any {
  try {
    const data = localStorage.getItem(`offline_${key}`);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to get offline data:', error);
    return null;
  }
}

// Clear offline data
export function clearOfflineData(key: string): void {
  try {
    localStorage.removeItem(`offline_${key}`);
  } catch (error) {
    console.error('Failed to clear offline data:', error);
  }
}